import { applyI18n } from '../i18n';

export interface FilesContextMenuItem {
  id: string;
  labelKey: string;
  disabled?: boolean;
  hidden?: boolean;
  action(): void;
}

export function openFilesContextMenu(input: {
  anchorRect: DOMRect;
  items: FilesContextMenuItem[];
  /** Mount target; defaults to the fullscreen element (if any) or document.body. */
  container?: HTMLElement;
}): void {
  closeFilesContextMenu();

  const backdrop = document.createElement('div');
  backdrop.className = 'files-context-menu-backdrop';
  backdrop.dataset.filesContextMenu = 'true';

  const menu = document.createElement('div');
  menu.className = 'files-context-menu';
  menu.setAttribute('role', 'menu');

  for (const item of input.items) {
    if (item.hidden) {
      continue;
    }

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'files-context-menu__item';
    button.setAttribute('role', 'menuitem');
    button.setAttribute('data-i18n', item.labelKey);
    button.disabled = item.disabled ?? false;
    button.addEventListener('click', () => {
      closeFilesContextMenu();
      if (!item.disabled) {
        item.action();
      }
    });
    menu.append(button);
  }

  backdrop.append(menu);
  const mount =
    input.container
    ?? (document.fullscreenElement instanceof HTMLElement ? document.fullscreenElement : document.body);
  mount.append(backdrop);
  applyI18n(menu);

  positionMenu(menu, input.anchorRect);

  const buttons = Array.from(menu.querySelectorAll<HTMLButtonElement>('button:not(:disabled)'));
  buttons[0]?.focus();

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      closeFilesContextMenu();
      return;
    }

    if (!['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key)) {
      return;
    }

    const active = document.activeElement instanceof HTMLButtonElement ? document.activeElement : null;
    const index = active ? buttons.indexOf(active) : -1;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      buttons[Math.min(buttons.length - 1, index + 1)]?.focus();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      buttons[Math.max(0, index - 1)]?.focus();
    } else if (event.key === 'Enter') {
      active?.click();
    }
  };

  const close = () => {
    window.removeEventListener('keydown', onKeyDown);
    backdrop.remove();
  };

  backdrop.addEventListener('click', (event) => {
    if (event.target === backdrop) {
      close();
    }
  });
  window.addEventListener('keydown', onKeyDown);
  backdrop.addEventListener('files-context-menu-close', close, { once: true });
}

export function closeFilesContextMenu(): void {
  const existing = document.querySelector<HTMLElement>('[data-files-context-menu="true"]');
  if (existing) {
    existing.dispatchEvent(new Event('files-context-menu-close'));
  }
}

function positionMenu(menu: HTMLElement, anchorRect: DOMRect): void {
  const menuWidth = 220;
  const menuHeight = Math.max(44, menu.childElementCount * 40 + 12);
  const left = Math.min(window.innerWidth - menuWidth - 8, Math.max(8, anchorRect.left));
  const top = Math.min(window.innerHeight - menuHeight - 8, Math.max(8, anchorRect.bottom + 4));
  menu.style.left = `${left}px`;
  menu.style.top = `${top}px`;
}
